import { useLocation, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";
import { getLogger } from "@/lib/logging/logger";
import { useStore } from "@/store";
import getFlow from "@/utils/getFlow";
import { networkManager } from "@/utils/networkManager";
import { parseQueryParams } from "@/utils/parseQueryParams";
import { type SearchParamsInput, toSearchParamsRecord } from "@/utils/searchParams";
import { buildSteps, getRouteForStep, type InternalStep } from "@/utils/steps";
import { getPathNameWithoutLocaleAndPartner, isHomePage } from "@/utils/urlHelpers";
import { useCreateSession } from "./useCreateSession";

const logger = getLogger();

// A rapid double-tap can advance two steps at once: the second tap either
// re-enters the still-mounted source page (during a pending transition) or lands
// on the next page's button in the same screen spot after it mounts. A short
// cooldown — module-scoped so it survives the step transition and is shared across
// every page — drops that stray second advance. It auto-expires, so a blocked or
// no-op navigation never dead-locks the button. Programmatic advances that
// legitimately chain (e.g. the instructions screen auto-skipping on mount) pass
// `immediate` to bypass it.
const ADVANCE_COOLDOWN_MS = 500;
let lastAdvanceAt = Number.NEGATIVE_INFINITY;

export default function useNavigationHelpers() {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { locale, partner } = useParams({ from: "/$locale/$partner" });
	const search = useSearch({ from: "/$locale/$partner/_app" });
	const { isFromHomeFlow } = getFlow();
	const enabledPages = useStore((state) =>
		isFromHomeFlow ? state.config?.pagesConfig.fromHome : state.config?.pagesConfig.fromStore,
	);
	const instructionsConfig = useStore((state) => state.config?.pagesConfig.instructions);
	const setTimer = useStore((s) => s.setTimer);
	const resetTimer = useStore((state) => state.resetTimer);
	const clearError = useStore((state) => state.clearError);
	const { getSessionId } = useCreateSession();

	const steps = buildSteps(enabledPages, isFromHomeFlow, instructionsConfig);
	const fallbackStep: InternalStep = steps[0] ?? "";
	const currentPage = getPathNameWithoutLocaleAndPartner(pathname) as InternalStep;
	const currentStepIndex = steps.indexOf(currentPage);
	const isFirstStep = currentPage === fallbackStep;
	const isLastStep = currentPage === (steps[steps.length - 1] ?? fallbackStep);
	const displayLanguageSelector = isFirstStep;

	const attachStaticParams = useCallback(
		(params: SearchParamsInput = {}, includeSessionParams: boolean = true) => {
			const defaultParams = parseQueryParams(search, { includeSession: includeSessionParams });
			return {
				...toSearchParamsRecord(params),
				...defaultParams,
			};
		},
		[search],
	);

	const networkAwarePush = useCallback(
		(step: InternalStep, params: SearchParamsInput = {}, includeSessionParams: boolean = true) => {
			const mergedSearch = attachStaticParams(params, includeSessionParams);
			const to = getRouteForStep(step);
			networkManager.runWhenOnline(() => navigate({ to, params: { locale, partner }, search: mergedSearch }));
		},
		[attachStaticParams, navigate, locale, partner],
	);

	const networkAwareBack = useCallback(() => {
		networkManager.runWhenOnline(() => window.history.back());
	}, []);

	const goToNextStep = useCallback(
		(params?: SearchParamsInput, options?: { immediate?: boolean }) => {
			if (!options?.immediate) {
				const now = performance.now();
				if (now - lastAdvanceAt < ADVANCE_COOLDOWN_MS) return;
				lastAdvanceAt = now;
			}
			if (currentStepIndex === 0) setTimer({ totalTimeout: 180, popupTimeout: 30 });
			else resetTimer();
			// find next step
			const nextStepIndex = (currentStepIndex + 1) % steps.length;
			const nextPage = steps[nextStepIndex] ?? fallbackStep;
			logger.info("FLOW", `Navigating to: ${nextPage}`);
			const shouldCreateSession = currentStepIndex === 0 && !isHomePage(pathname);
			networkManager.runWhenOnline(async () => {
				if (shouldCreateSession) await getSessionId();
				const mergedSearch = attachStaticParams(params ?? {}, true);
				const to = getRouteForStep(nextPage);
				navigate({ to, params: { locale, partner }, search: mergedSearch });
			});
		},
		[
			attachStaticParams,
			currentStepIndex,
			fallbackStep,
			getSessionId,
			locale,
			navigate,
			partner,
			pathname,
			resetTimer,
			setTimer,
			steps,
		],
	);

	const goToFirstPage = useCallback(() => {
		logger.info("FLOW", "Navigating to first page");
		clearError();
		networkAwarePush(fallbackStep, {}, false);
	}, [clearError, fallbackStep, networkAwarePush]);

	const suppressHome = isHomePage(pathname) && !steps.includes("");

	return {
		steps,
		displayLanguageSelector,
		suppressHome,
		isFirstStep,
		isLastStep,
		currentPage,
		networkAwarePush,
		networkAwareBack,
		goToNextStep,
		goToFirstPage,
	};
}
